import { Component, Input, OnInit } from '@angular/core';
import { ObservationsKpiData as ObservationsKpiData } from '../../../types/service-insight-types';

/**
 * Summary component for displaying a single observation KPI card in the Service Insights table.
 * It is designed to be used within the Service Insights table to provide a quick overview of key observations.
 * Color of the card is determined by its sort order relative to the total number of items, providing a visual cue for prioritization.
 */
@Component({
    selector: 'observations-kpi-card',
    templateUrl: './observations-kpi-card.component.html',
    styleUrls: ['./observations-kpi-card.component.scss'],
    standalone: false
})
export class ObservationsKpiCardComponent implements OnInit {
  @Input() id: string | undefined;
  @Input() observationsData: ObservationsKpiData | undefined;
  @Input() totalItems: number | undefined;

  cardColor = 'bg-neptune-color-primary-700';

  /**
   * Card colors as defined in the Figma by UI/UX team.
   * The colors are ordered from darkest to lightest, representing the severity of the observation.
   */
  readonly neptuneCardColors = [
    'bg-neptune-color-primary-700', // darkest
    'bg-neptune-color-primary-600',
    'bg-neptune-color-primary-500',
    'bg-neptune-color-primary-400',
    'bg-neptune-color-primary-300', // lightest
  ];

  ngOnInit() {
    // Sets the color of the card based on severity
    if (this.observationsData && this.totalItems) {
      this.cardColor = this.getColor(
        this.observationsData.sortOrder,
        this.totalItems,
      );
    }
  }

  /**
   * Returns the Neptune color class corresponding to an item's rank.
   *
   * Items are distributed across the available color palette from darkest
   * to lightest based on their relative position within the ranked list:
   * - Rank 1 (highest priority) receives the darkest color.
   * - The lowest-ranked item receives the lightest color.
   * - Intermediate ranks are mapped proportionally to the remaining colors.
   *
   * When there are more ranked items than available colors, multiple ranks
   * may share the same color. When there is only one item, the darkest
   * color is always returned.
   *
   * @param rank The 1-based sort order of the item, where 1 is the highest priority.
   * @param totalItems The total number of ranked items.
   * @returns A Neptune color CSS class name from the available color palette.
   */
  private getColor(rank: number, totalItems: number): string {
    if (totalItems <= 1) {
      return this.neptuneCardColors[0];
    }

    const index = Math.round(
      ((rank - 1) * (this.neptuneCardColors.length - 1)) / (totalItems - 1),
    );

    return this.neptuneCardColors[
      Math.min(index, this.neptuneCardColors.length - 1)
    ];
  }
}
